import { Box, Tab, Tabs } from '@mui/material';
import type { SxProps,Theme } from '@mui/material/styles';
import type { ReactNode, SyntheticEvent } from 'react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from '../../ui/Section';

export interface TabDefinition {
  /**
   * Optional stable identifier.
   *
   * Provide this when:
   * - using controlled tab selection;
   * - using initialTabId;
   * - tracking the selected tab externally;
   * - tabs can be reordered dynamically.
   *
   * For simple usage, it can be omitted.
   */
  id?: string;

  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabbedSectionProps {
  tabs: readonly TabDefinition[];

  /**
   * Controlled selected tab ID.
   *
   * Tabs used with controlled selection should provide explicit IDs.
   */
  activeTabId?: string;

  /**
   * Initial selected tab ID for uncontrolled usage.
   *
   * The corresponding tab should provide an explicit ID.
   */
  initialTabId?: string;

  /** Optional accessible name; shared fallback keeps simple consumers concise. */
  ariaLabel?: string;
  contentPadding?: boolean;

  contentSx?: SxProps<Theme>;
  sectionSx?: SxProps<Theme>;
  tabBarSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;

  /**
   * Called whenever the selected tab changes.
   *
   * When a tab does not provide an ID, the component-generated ID
   * is returned.
   */
  onTabChange?: (tabId: string) => void;
}

interface ResolvedTabDefinition extends TabDefinition {
  resolvedId: string;
}

const getFirstEnabledTab = (
  tabs: readonly ResolvedTabDefinition[],
): ResolvedTabDefinition | null => (
  tabs.find((tab) => !tab.disabled) ?? null
);

/**
 * Combines MUI sx values without creating nested sx arrays.
 */
const combineSx = (
  ...values: Array<SxProps<Theme> | undefined>
): SxProps<Theme> => (
  values.flatMap((value) => {
    if (!value) {
      return [];
    }

    return Array.isArray(value)
      ? [...value]
      : [value];
  }) as SxProps<Theme>
);

/**
 * Shared tab navigation and content surface.
 *
 * Feature pages own:
 * - Tab definitions and visibility
 * - Tab content
 * - Permissions
 * - API calls
 * - Form state
 * - Search and pagination
 * - Business rules
 *
 * This component owns:
 * - Tab selection
 * - Accessibility wiring
 * - Horizontal tab scrolling
 * - Shared tab presentation
 * - Shared outer content spacing
 */
export const TabbedSection = ({
  tabs,
  activeTabId,
  initialTabId,
  ariaLabel,
  contentPadding = true,
  contentSx,
  sectionSx,
  tabBarSx,
  sx,
  onTabChange,
}: TabbedSectionProps) => {
  const { t } = useTranslation('common');
  const accessibleLabel = ariaLabel ?? t('sections');
  const componentId = useId();

  /**
   * Preserve explicitly supplied IDs.
   *
   * For tabs without an ID, generate an internal ID so developers can
   * provide only a label and content for normal uncontrolled usage.
   */
  const resolvedTabs = useMemo<ResolvedTabDefinition[]>(
    () => tabs.map((tab, index) => ({
      ...tab,
      resolvedId: tab.id ?? `${componentId}-item-${index}`,
    })),
    [componentId, tabs],
  );

  const enabledTabs = useMemo(
    () => resolvedTabs.filter((tab) => !tab.disabled),
    [resolvedTabs],
  );

  const getInitialSelectedTabId = useCallback(() => {
    const requestedInitialTab = initialTabId
      ? resolvedTabs.find(
        (tab) => (
          tab.resolvedId === initialTabId
            && !tab.disabled
        ),
      )
      : null;

    return (
      requestedInitialTab?.resolvedId
      ?? getFirstEnabledTab(resolvedTabs)?.resolvedId
      ?? null
    );
  }, [
    initialTabId,
    resolvedTabs,
  ]);

  const [internalSelectedTabId, setInternalSelectedTabId] = useState<
    string | null
  >(() => {
    const requestedInitialTab = initialTabId
      ? resolvedTabs.find(
        (tab) => (
          tab.resolvedId === initialTabId
            && !tab.disabled
        ),
      )
      : null;

    return (
      requestedInitialTab?.resolvedId
      ?? getFirstEnabledTab(resolvedTabs)?.resolvedId
      ?? null
    );
  });

  const isControlled = activeTabId !== undefined;

  const requestedTabId = isControlled
    ? activeTabId
    : internalSelectedTabId;

  const selectedTab = resolvedTabs.find(
    (tab) => (
      tab.resolvedId === requestedTabId
      && !tab.disabled
    ),
  );

  const activeTab = selectedTab ?? enabledTabs[0] ?? null;
  const selectedTabId = activeTab?.resolvedId ?? null;

  /** If selected tab is removed or disabled, select first enabled tab. */
  useEffect(() => {
    if (isControlled) {
      return;
    }

    const currentTabIsValid = internalSelectedTabId
      && enabledTabs.some(
        (tab) => tab.resolvedId === internalSelectedTabId,
      );

    if (currentTabIsValid) {
      return;
    }

    const nextTabId = getInitialSelectedTabId();

    if (nextTabId !== internalSelectedTabId) {
      setInternalSelectedTabId(nextTabId);

      if (nextTabId) {
        onTabChange?.(nextTabId);
      }
    }
  }, [
    enabledTabs,
    getInitialSelectedTabId,
    internalSelectedTabId,
    isControlled,
    onTabChange,
  ]);

  if (resolvedTabs.length === 0 || !activeTab) {
    return null;
  }

  const handleTabChange = (
    _event: SyntheticEvent,
    tabId: string,
  ) => {
    const nextTab = resolvedTabs.find(
      (tab) => (
        tab.resolvedId === tabId
        && !tab.disabled
      ),
    );

    if (!nextTab) {
      return;
    }

    if (!isControlled) {
      setInternalSelectedTabId(tabId);
    }

    onTabChange?.(tabId);
  };

  const getTabElementId = (tabId: string) => (
    `${componentId}-tab-${tabId}`
  );

  const getPanelElementId = (tabId: string) => (
    `${componentId}-panel-${tabId}`
  );

  const panelPadding: SxProps<Theme> = contentPadding
    ? {
      p: 9,
    }
    : {};

  return (
    <Box sx={sx}>
      <Box
        sx={combineSx(
          {
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            mt: 0,
            mb: '-1px',
            borderBottom: 0,
          },
          tabBarSx,
        )}
      >
        <Tabs
          value={selectedTabId ?? false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={accessibleLabel}
          sx={{
            position: 'relative',
            borderBottom: 0,
            '& .MuiTabs-scroller': {
              margin: 0,
              paddingLeft: 1,
              paddingRight: 1,
              boxSizing: 'border-box',
            },
            '& .MuiTabs-scrollButtons': {
              top: 0,
              bottom: 0,
              zIndex: 1,
              width: 32,
              '&.Mui-disabled': {
                display: 'none',
              },
            },
            '& .MuiTabs-scrollButtons:first-of-type': {
              left: 0,
            },
            '& .MuiTabs-scrollButtons:last-of-type': {
              right: 0,
            },
          }}
        >
          {resolvedTabs.map((tab) => (
            <Tab
              key={tab.resolvedId}
              id={getTabElementId(tab.resolvedId)}
              aria-controls={getPanelElementId(tab.resolvedId)}
              value={tab.resolvedId}
              label={tab.label}
              disabled={tab.disabled}
            />
          ))}
        </Tabs>

      </Box>

      {/*
       * sectionSx stays on a wrapping Box because the custom Section
       * accepts one style object rather than MUI sx arrays.
       */}
      <Box sx={sectionSx}>
        <Section
          style={{
            mt: 0,
            p: 0,
          }}
        >
          <Box
            role="tabpanel"
            id={getPanelElementId(activeTab.resolvedId)}
            aria-labelledby={getTabElementId(activeTab.resolvedId)}
            sx={combineSx(
              {
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden',
              },
              panelPadding,
              contentSx,
            )}
          >
            {activeTab.content}
          </Box>
        </Section>
      </Box>
    </Box>
  );
};